import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Linkify from "linkify-react";
import { useStoreState, Session } from "ezStore";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Button, Card, FormGroup, Tag, TagSize } from "@cofano/cck";
import { Box, List, Tab, Tabs, Typography, Grid2 as Grid } from "@mui/material";
import SdCardAlert from "@mui/icons-material/SdCardAlert";
import AddRounded from "@mui/icons-material/AddRounded";
import AirlineStops from "@mui/icons-material/AirlineStops";
import Anchor from "@mui/icons-material/Anchor";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Balance from "@mui/icons-material/Balance";
import DirectionsBoat from "@mui/icons-material/DirectionsBoat";
import Edit from "@mui/icons-material/Edit";
import MoreVert from "@mui/icons-material/MoreVert";
import ViewHeadline from "@mui/icons-material/ViewHeadline";
import Widgets from "@mui/icons-material/Widgets";
import History from "@mui/icons-material/History";

import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";
import { FormDialog } from "components/dialogs/Dialogs";
import CargoActualsForm from "components/cargoaction/CargoActualsForm";
import LetterOfProtestDialog from "components/cargo/LetterOfProtestDialog";
import useLayout from "components/layout/useLayout";
import useTabs from "components/hooks/useTabs";
import EntityHistoryDialog from "components/entityHistory/components/dialogs/EntityHistoryDialog";
import ExpandableText from "components/text/ExpandableText";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "utils";
import { DossierLink, LocationLink, ProductLink, RelationLink, ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";
import { cargoActualsComparison, getCallTypeColor } from "utils/cargoActionUtils";

import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { StatusCargoEntity } from "view/statuses/types/status";
import { Charter } from "view/charters/types/charter";
import { PdfButton } from "view/charters/dossier/ship/ShipDetailsBlock";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";
import ConnectingVisitField from "view/cargos/components/connectingVisits/ConnectingVisitField";
import CargoServiceIcons from "view/cargos/components/CargoServiceIcons";
import { cargoHasServices } from "view/cargos/utils/CargoUtils";
import CargoActionQuantity from "view/cargos/components/quantities/CargoActionQuantity";
import { TransportDocumentPdfButton } from "view/cargos/components/download/TransportDocumentPdfButton";
import { SdsDocumentButton } from "view/cargos/components/download/SdsDocumentButton";
import { CargoActualsOutput } from "view/cargos/utils/schema";
import CallCreateEditDialog from "view/calls/components/dialogs/CallCreateEditDialog";
import LastShippedProductsPopover from "view/products/components/popover/LastShippedProductsPopover";
import ContainerAmountSummary from "view/containers/components/summary/ContainerAmountSummary";

import { actionTypeToDocumentType, VoyageTabs } from "view/voyages/utils/VoyageUtils";
import VoyageDocuments from "view/voyages/components/documents/VoyageDocuments";
import VoyagePurchaseChartersDetails from "view/voyages/components/details/VoyagePurchaseChartersDetails";
import VoyageDetailsCallContainersBlock from "view/voyages/components/details/VoyageDetailsCallContainersBlock";
import VoyageScopeDetails from "view/voyages/components/details/VoyageScopeDetails";
import VoyageLogDetails from "view/voyages/components/details/VoyageLogDetails";
import VoyageCallTimesDetails from "view/voyages/components/details/VoyageCallTimesDetails";
import VoyageReefersTable from "view/voyages/components/table/VoyageReefersTable";
import VoyagePhoneContactsPopover from "view/voyages/components/popover/VoyagePhoneContactsPopover";
import VoyageEditDialog from "view/voyages/components/dialogs/VoyageEditDialog";
import VoyageCallUpdateTimesAndLocationDialog from "view/voyages/components/dialogs/VoyageCallUpdateTimesAndLocationDialog";
import VoyagePlanCargoActions from "view/voyages/components/planning/VoyagePlanCargoActions";
import VoyageCargoActionTimesheet from "view/voyages/components/timesheet/VoyageCargoActionTimesheet";
import TankList from "view/voyages/components/cargotanks/TankList";
import VoyageProgressBar from "view/voyages/components/progress/VoyageProgressBar";

import { PluginFields } from "types/enums";
import { BargeType, CharterPdfType, HistoryEntity, CallType, DocumentEntity, StatusType } from "__generated__/enums";
import { SaveCargoMeasurementsDocument } from "__generated__/view/cargos/graphql/cargo.mutations.generated";
import { StatusFragmentFragment as StatusFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import {
	VoyageCallListVoyageFragment,
	VoyageCallTimesBlockFragment,
	VoyageCargoActionDetailsFragment,
	VoyageCargoActionDetailsVoyageFragment,
	VoyageCargoActionListCargoActionFragment,
	VoyageDetailsTabFragment,
	VoyageEditDialogCargoFragment,
	VoyageInfoFragment,
	VoyageSummaryVoyageFragment,
} from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";

interface VoyageInfoProps {
	voyage: VoyageInfoFragment;
	refetch: () => void;
	tabsQueryParameterName?: string;
	withoutTabs?: boolean;
	tabSuffix?: ReactNode;
}

export const VoyageInfo = ({
	voyage,
	refetch,
	tabsQueryParameterName,
	withoutTabs = false,
	tabSuffix,
}: VoyageInfoProps) => {
	const session = useStoreState(store => store.session);

	const location = useLocation();

	const { isRTR } = usePermission();

	const { selectedTab, selectTab } = useTabs<VoyageTabs>({
		defaultSelected: location.state?.tab || VoyageTabs.Details,
		param: tabsQueryParameterName,
	});

	return (
		<Grid container spacing={2}>
			{!withoutTabs && (
				<Grid size={12}>
					<Card disablePadding>
						<Box
							sx={{
								alignItems: "center",
								display: "flex",
								justifyContent: "space-between",
							}}>
							<Tabs
								value={selectedTab}
								indicatorColor="primary"
								scrollButtons="auto"
								onChange={(_, value) => selectTab(value)}
								variant="scrollable">
								<Tab
									label={`${intl.formatMessage({ id: "DETAILS" })} & ${intl.formatMessage({ id: "CALLS" })}`}
									value={VoyageTabs.Details} />

								<Tab
									disabled={voyage.purchaseCharters?.length === 0}
									label={intl.formatMessage({ id: "PURCHASE_CHARTERS" })}
									value={VoyageTabs.Charters} />

								{((hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeIn")) || hasOptionalFieldPermission(session, Fields.VOYAGE, "scopeOut")) && (
									<Tab label={intl.formatMessage({ id: "VOYAGE_SCOPE" })} value={VoyageTabs.Scope} />
								)}

								{hasPluginPermission(session, "reeferChecks") && (
									<Tab label={intl.formatMessage({ id: "REEFER" })} value={VoyageTabs.Reefer} />
								)}

								<Tab label={intl.formatMessage({ id: "VOYAGELOG" })} value={VoyageTabs.VoyageLog} />

								{isRTR && hasPluginPermission(session, "callDragAndDropPlanning") && (
									<Tab label={intl.formatMessage({ id: "PLANNING" })} value={VoyageTabs.Planning} />
								)}

								<Tab label={intl.formatMessage({ id: "DOCUMENTS" })} value={VoyageTabs.Documents} />
							</Tabs>

							{tabSuffix}
						</Box>
					</Card>
				</Grid>
			)}

			{selectedTab === VoyageTabs.Details && (
				<Grid size={12}>
					<Details voyage={voyage} refetch={refetch} />
				</Grid>
			)}

			{selectedTab === VoyageTabs.Charters && (
				<Grid size={{ lg: 4, md: 4, xs: 12 }}>
					<VoyagePurchaseChartersDetails charters={voyage.purchaseCharters ?? []} />
				</Grid>
			)}

			{selectedTab === VoyageTabs.Scope && (
				<Grid size={12}>
					<VoyageScopeDetails voyage={voyage} />
				</Grid>
			)}

			{selectedTab === VoyageTabs.Reefer && (
				<Grid size={12}>
					<VoyageReefersTable voyage={voyage} />
				</Grid>
			)}

			{selectedTab === VoyageTabs.VoyageLog && (
				<Grid size={12}>
					<VoyageLogDetails voyageId={voyage.id} />
				</Grid>
			)}

			{isRTR && selectedTab === VoyageTabs.Planning && (
				<Grid size={12}>
					<VoyagePlanCargoActions voyageId={voyage.id} />
				</Grid>
			)}

			{selectedTab === VoyageTabs.Documents && (
				<Grid size={12}>
					<VoyageDocuments voyage={voyage} />
				</Grid>
			)}
		</Grid>
	);
};

interface VoyageDetailsProps {
	voyage: VoyageDetailsTabFragment;
	refetch: () => void;
}

const Details = ({ voyage, refetch }: VoyageDetailsProps) => {
	const session = useStoreState(store => store.session);

	const location = useLocation();

	const useContainers = hasPluginPermission(session, "useContainers");
	const showTimesheet = hasPluginPermission(session, "cargoTimesheet");

	// by default open the first call that is not finished, or otherwise the first call
	const firstUnfinishedCall = voyage.calls
		.toSorted((a, b) => (a.pta ?? 0) - (b.pta ?? 0))
		.find(call => !call.lastStatusChange?.status.finishedStatus) ?? voyage.calls[0];

	const callFromState = voyage.calls.find(call => call.id === location.state?.selectedCallId);

	const [ selectedCallId, setSelectedCallId ] = useState<number | null>(callFromState?.id ?? firstUnfinishedCall?.id ?? null);
	const [ selectedActionId, setSelectedActionId ] = useState<number | null>(null);

	const selectedCall = voyage.calls.find(call => call.id === selectedCallId);
	const selectedAction = selectedCall?.cargos
		?.find(action => action.id === selectedActionId);

	const cargos = voyage.calls.flatMap(call => call.cargos)
		.map(cargo => cargo.cargo);

	const showLoadingDocumentsWarning = selectedAction?.type == CallType.Discharging && !voyage.calls
		.flatMap(call => call.cargos)
		.flatMap(cargoAction => cargoAction.documents)
		?.some(document => document?.documentEntity == DocumentEntity.LoadingCargo);

	const documents = selectedAction && <VoyageDocuments
		voyage={voyage}
		key={selectedAction.id}
		subtitle={selectedAction.type && intl.formatMessage({ id: selectedAction.type })}
		warning={showLoadingDocumentsWarning ? intl.formatMessage({ id: "DOCUMENTS_LOADING_NOT_COMPLETED_WARNING" }) : ""}
		entities={[ {
			id: selectedAction.cargo?.id ?? 0,
			cargoActionId: selectedAction.id,
			name: `${selectedAction.location?.description} - ${selectedAction.location?.displayCode || selectedAction.location?.code || ""} (${selectedAction.type && (intl.formatMessage({ id: selectedAction.type }))})`,
			type: selectedAction.type && actionTypeToDocumentType(selectedAction.type) || DocumentEntity.LoadingCargo,
			callId: selectedCallId!,
		}, {
			id: selectedAction.cargo?.id ?? 0,
			name: selectedAction.cargo.cargoNumber ?? selectedAction.cargo.containerNumber ?? selectedAction.cargo.product?.name ?? intl.formatMessage({ id: "CARGO" }),
			type: DocumentEntity.Cargo,
			blockUpload: true, // cargo documents should not be uploaded through this screen
		} ]}
		refetch={refetch}
	/>;

	return (
		<div style={{ flexGrow: 1 }}>
			<Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
				<Grid size={{ lg: 4, md: 6, xs: 12 }}>
					<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
						<VoyageSummary
							voyage={voyage}
							voyageCargos={cargos}
							refetch={refetch}
							session={session}
						/>

						<CallList
							voyage={voyage}
							refetch={refetch}
							selectedActionId={selectedActionId}
							selectedCallId={selectedCallId}
							setSelectedCallId={setSelectedCallId}
							setSelectedActionId={setSelectedActionId}
						/>
					</div>
				</Grid>
				<Grid container size={{ lg: 8, md: 6, xs: 12 }} className="call-cargo-overview">
					{!useContainers && (
						<>
							<Grid size={{ lg: 6, md: 12, xs: 12 }}>
								<div style={{ display: "flex", flexGrow: 1, flexDirection: "column", gap: 16 }}>
									{selectedCall && selectedCall.cargos && selectedCall.cargos.length > 0 && (
										<CargoActions
											voyage={voyage}
											cargoActions={selectedCall.cargos}
											selectedCall={selectedCall}
											selectedAction={selectedAction}
											setSelectedCallId={setSelectedCallId}
											setSelectedActionId={setSelectedActionId}
											refetch={refetch}
										/>
									)}

									{showTimesheet && selectedAction && documents}

									{voyage.ship.bargeType === BargeType.Tanker && (
										<TankList
											voyageId={voyage.id}
											shipId={voyage.ship.id}
											cargos={cargos}
											selectedCargo={{ id: selectedAction?.cargo?.id }}
											mayAssign={selectedAction?.type == CallType.Loading}
											refetch={refetch}
										/>
									)}
								</div>
							</Grid>
							<Grid size={{ lg: 6, md: 12, xs: 12 }}>
								<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
									{selectedCall && (
										<CallTimes call={selectedCall} refetch={refetch} />
									)}

									{showTimesheet && selectedAction && (
										<VoyageCargoActionTimesheet
											cargoAction={selectedAction}
											refetch={refetch}
											asOverrideSkipperTimes={false}
										/>
									)}

									{!showTimesheet && selectedAction && documents}
								</div>
							</Grid>
						</>
					)}

					{useContainers && selectedCall && (
						<>
							<VoyageDetailsCallContainersBlock
								call={selectedCall}
								hideExpectedCargos={voyage.isBallast || !hasOptionalFieldPermission(session, Fields.VOYAGE, "expectedCargos")}
								refetch={refetch}
							/>

							<Grid size={{ xs: 12 }}>
								<VoyageDocuments voyage={voyage} refetch={refetch} />
							</Grid>
						</>
					)}
				</Grid>
			</Grid>
		</div>
	);
};

interface VoyageSummaryProps {
	voyage: VoyageSummaryVoyageFragment;
	voyageCargos: VoyageEditDialogCargoFragment[];
	session: Session;
	refetch: () => void;
}

const VoyageSummary = ({ voyage, voyageCargos, refetch, session }: VoyageSummaryProps) => {

	const { isRTR, isSkipperOrShipManager, isReadOnly } = usePermission();

	const [ editDialogOpen, setEditDialogOpen ] = useState(false);

	const isBallastEnabled = hasOptionalFieldPermission(session, Fields.VOYAGE, "isBallast");
	const voyageScheduleEnabled = hasOptionalFieldPermission(session, Fields.VOYAGE, "schedule");
	const referenceEnabled = hasOptionalFieldPermission(session, Fields.VOYAGE, "reference");
	const showVoyageEdit = isBallastEnabled || voyageScheduleEnabled || referenceEnabled;

	const references = referenceEnabled ?
		voyageCargos
			.map(cargo => cargo.charter)
			.filter((charter, index, self) => charter != null && index === self.findIndex(c => c?.id === charter.id))
			.map(charter => ({
				id: charter!.id,
				charterNumber: charter!.charterNumber,
				reference: charter!.purchaseCharter?.customerReference,
			})).filter(reference => reference.reference)
		: [];

	const gridSize: Parameters<typeof Grid>[0]["size"] = {
		xs: 6,
		md: isBallastEnabled && voyageScheduleEnabled ? 4 : 6,
	};

	return (
		<Card
			icon={<DirectionsBoat fontSize="small" color="primary" />}
			title={<VoyageLink voyage={voyage} />}
			subtitle={<ShipLink ship={voyage.ship } />}
			actions={[ {
				render: () => (
					<Box marginRight={1}>
						<StatusChanger
							session={session}
							entity={voyage}
							statusType={StatusType.Voyage}
							refetch={refetch}
							showHistory
						/>
					</Box>
				),
			}, {
				render: () => <PdfButton
					charter={voyage.cargoCharter!}
					type={CharterPdfType.NominationToSkipper}
				/>,
				hidden: !voyage.cargoCharter,
			}, {
				onClick: () => setEditDialogOpen(true),
				hidden: !(isRTR || isSkipperOrShipManager) || isReadOnly || !showVoyageEdit,
				icon: <Edit fontSize="small" />,
			} ]}
		>
			<div style={{ borderBottom: "2px solid #ddd", paddingBottom: 16, marginBottom: 16 }}>
				<Grid container spacing={2}>
					<Grid size={gridSize}>
						<FormGroup label={intl.formatMessage({ id: "CREDITOR" })}>
							{isRTR ? <RelationLink relation={voyage.creditor} /> : voyage.creditor?.name}
						</FormGroup>
					</Grid>
					{voyageScheduleEnabled && (
						<Grid size={gridSize}>
							<FormGroup label={Language("VOYAGE_SCHEDULE")}>
								{voyage.schedule?.schedule ?? "-"}
							</FormGroup>
						</Grid>
					)}
					{isBallastEnabled && (
						<Grid size={gridSize}>
							<FormGroup label={Language("IS_BALLAST")}>
								{DataFormat.boolean(voyage.isBallast)}
							</FormGroup>
						</Grid>
					)}

					{references.map(reference => (
						<Grid size={gridSize} key={reference.id}>
							<FormGroup label={`${intl.formatMessage({ id: "REFERENCE" })} (${reference.charterNumber})`}>
								{reference.reference}
							</FormGroup>
						</Grid>
					))}
				</Grid>
			</div>

			<div style={{ padding: "0px 8px" }}>
				<VoyageProgressBar voyage={voyage} />
			</div>

			<VoyageEditDialog
				open={editDialogOpen}
				onClose={() => setEditDialogOpen(false)}
				voyage={voyage}
				cargos={voyageCargos}
				refetch={refetch}
				session={session}
			/>
		</Card>
	);
};

interface CallListProps {
	voyage: VoyageCallListVoyageFragment;
	selectedCallId: number | null;
	setSelectedCallId: (id: number | null) => void;
	selectedActionId: number | null;
	setSelectedActionId: (id: number | null) => void;
	refetch: () => void;
}

const CallList = ({
	voyage,
	refetch,
	selectedCallId,
	setSelectedCallId,
	selectedActionId,
	setSelectedActionId,
}: CallListProps) => {

	const orderedCalls = voyage.calls.toSorted((a, b) => (a.pta ?? 0) - (b.pta ?? 0));
	const session = useStoreState(store => store.session);
	const useContainers = hasPluginPermission(session, "useContainers");
	const { isAdmin, isCompanyUser, isReadOnly } = usePermission();

	const [ addCallDialogOpen, setAddCallDialogOpen ] = useState(false);

	useEffect(() => {
		if (selectedCallId) {
			// requestAnimationFrame ensures the DOM update from the state change
			// has actually painted before we try to find the element.
			requestAnimationFrame(() => {
				const element = document.getElementById(`call-item-${selectedCallId}`);

				if (element) {
					element.scrollIntoView({
						behavior: "smooth",
						block: "nearest",
						inline: "start",
					});
				}
			});
		}
	}, [ selectedCallId ]);

	return (
		<Card
			disablePadding
			icon={<ViewHeadline fontSize="small" color="primary" />}
			title={intl.formatMessage({ id: "CALLS" })}
			actions={[ {
				onClick: () => setAddCallDialogOpen(true),
				icon: <AddRounded />,
				hidden: !useContainers || isReadOnly || !(isAdmin || isCompanyUser),
			} ]}
		>
			{orderedCalls.length > 0 ? (
				<FormArrayContainer>
					<Box className="voyage-info-ordered-calls">
						{voyage.from?.id != orderedCalls[0].location?.id && (
							<>
								<VoyageBoundary location={voyage.from} timestamp={voyage.start} />
								<div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
									<Tab
										value={"none"}
										label={<MoreVert />}
										disabled />
								</div>
							</>
						)}

						{orderedCalls.map((call, index) => {
							const shouldBeCollapsed = useContainers ? index !== 0 : false;
							return (
								<div key={call.id}
									id={`call-item-${call.id}`}>
									<FormArrayRow
										color={selectedCallId === call.id ? "#F39200" : "#ddd"}
										title={<LocationLink location={call.location} />}
										collapsable
										collapseOnlyOnIcon
										collapsed={shouldBeCollapsed}
										subtitle={(!call.simulatedTimestamp || call.ata || call.eta || call.atd || call.etd) && (
											<Box>
												<FormattedDateWindow
													from={call.ata ?? call.eta ?? (!call.simulatedTimestamp ? call.pta : undefined)}
													to={call.atd ?? call.etd ?? (!call.simulatedTimestamp ? call.ptd : undefined)}
													withoutYear />
											</Box>
										)}
										onCustomActions={[
											{
												render: () => (
													<Box marginRight={1}>
														<StatusChanger
															session={useStoreState(store => store.session)}
															entity={call}
															statusType={StatusType.Call}
															refetch={refetch}
															showHistory
														/>
													</Box>
												),
											},
										]}
										slotProps={{
											root: {
												sx: {
													marginBottom: 0,
													cursor: useContainers ? "pointer" : undefined,
													"& .MuiAccordionSummary-root": {
														transition: "background-color 0.15s ease",
													},
													...(selectedCallId === call.id ? {} : {
														"& .MuiAccordionSummary-root:hover": {
															backgroundColor: "rgba(243, 146, 0, 0.08)",
														},
													}),
												},
												onClick: () => {
													setSelectedCallId(call.id);
													setSelectedActionId(call.cargos[0]?.id);
												},
											},
										}}
									>
										{!useContainers ? (
											call.cargos != null ? (
												<CargoActionList
													cargoActions={call.cargos}
													charter={call.cargos[0]?.cargo.charter}
													selectedActionId={selectedActionId}
													setSelectedActionId={actionId => {
														setSelectedCallId(call.id);
														setSelectedActionId(actionId);
													}} />
											) : (
												<Box p={2} sx={{ display: "flex", justifyContent: "center" }}>
													<Typography
														component="span"
														color="grey"
														fontSize={14}
														fontStyle="italic">
														{intl.formatMessage({ id: "NO_ACTIONS_FOUND" })}
													</Typography>
												</Box>
											)
										) : (
											<>
												{!voyage.isBallast && hasOptionalFieldPermission(session, Fields.VOYAGE, "expectedCargos") && (
													<Box marginBottom={2}>
														<FormGroup
															labelPlacement="left-center"
															label={<b>{Language("EXPECTED")}</b>}
														/>
														<ContainerAmountSummary
															loadingAmount={call.expectedLoadingAmount ?? null}
															dischargingAmount={call.expectedDischargingAmount ?? null}
															loadingTeu={call.expectedLoadingTeu ?? null}
															dischargingTeu={call.expectedDischargingTeu ?? null}
														/>
													</Box>
												)}
												<Box>
													<FormGroup
														labelPlacement="left-center"
														label={<b>{Language("ACTUAL")}</b>}
													/>
													<ContainerAmountSummary
														loadingAmount={call.nrLoadingCargos ?? null}
														dischargingAmount={call.nrDischargingCargos ?? null}
														loadingTeu={call.callLoadingTeu ?? null}
														dischargingTeu={call.callDischargingTeu ?? null}
													/>
												</Box>
											</>
										)}
									</FormArrayRow>
									{index + 1 !== orderedCalls.length && (
										<Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
											<Tab
												value={"none"}
												label={<ArrowDownward />}
												disabled />
										</Box>
									)}
								</div>
							);
						})}

						{voyage.to?.id != orderedCalls[orderedCalls.length - 1].location?.id && (
							<>
								<div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
									<Tab
										value={"none"}
										label={<MoreVert />}
										disabled />
								</div>
								<VoyageBoundary location={voyage.to} timestamp={voyage.end} />
							</>
						)}
					</Box>
				</FormArrayContainer>
			) : (
				<Box p={2} sx={{ display: "flex", justifyContent: "center" }}>
					<Typography
						component="span"
						color="grey"
						fontSize={14}
						fontStyle="italic">
						{intl.formatMessage({ id: "NO_CALLS_FOUND" })}
					</Typography>
				</Box>
			)}

			<CallCreateEditDialog
				open={addCallDialogOpen}
				voyageId={voyage.id}
				onClose={() => setAddCallDialogOpen(false)}
				refetch={refetch}
				call={null}
			/>
		</Card>
	);
};

type VoyageBoundaryProps = {
	location?: VoyageCallListVoyageFragment["from"];
	timestamp: number | null | undefined;
}

const VoyageBoundary = ({ location, timestamp }: VoyageBoundaryProps) => {
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				border: "2px solid #ddd",
				padding: 8,
				color: "#888",
				textTransform: "uppercase",
			}}>
			<div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
				<b><LocationLink location={location} /></b>
				<small>
					<FormattedDate date={timestamp} withoutYear />
				</small>
			</div>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Anchor htmlColor="#aaa" />
			</div>
		</div>
	);
};

interface CargoActionListProps {
	cargoActions: VoyageCargoActionListCargoActionFragment[];
	selectedActionId: number | null;
	charter?: Pick<Charter, "id" | "charterNumber" | "type"> | null;
	setSelectedActionId: (id: number | null) => void;
}

const CargoActionList = ({
	cargoActions,
	selectedActionId,
	charter,
	setSelectedActionId,
}: CargoActionListProps) => {
	const { isRTR } = usePermission();

	return (
		<List disablePadding>
			{cargoActions
				.toSorted((a, b) => (a.start ?? 0) - (b.start ?? 0))
				.map((action) => (
					<Box
						key={action.id}
						sx={{
							border: 1,
							borderColor: "divider",
							borderLeftWidth: 4,
							borderLeftColor: selectedActionId === action.id ? "#F39200" : "#ddd",
							padding: 1,
							cursor: "pointer",
							transition: "background-color 0.15s ease",
							"&:hover": selectedActionId === action.id ? undefined : {
								backgroundColor: "rgba(243, 146, 0, 0.08)",
							},
						}}
						onClick={(e) => {
							e.stopPropagation();
							setSelectedActionId(action.id);
						}}>
						<div>
							<small style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<span style={{ display: "flex", alignItems: "center", gap: 8 }}>
									{action.cargo?.cargoNumber && (
										<span>{action.cargo?.cargoNumber}</span>
									)}
									<Tag
										color={getCallTypeColor(action.type)}
										text={intl.formatMessage({ id: action.type as string })}
										size={TagSize.SMALL}
									/>
									<span>{action.cargo?.product?.name}</span>
								</span>
								<div style={{ display: "flex", alignItems: "center" }}>
									{isRTR ? <DossierLink charter={charter} /> : charter?.charterNumber}
								</div>
							</small>
						</div>
					</Box>
				))}
		</List>
	);
};

const getCargoActionLabel = (cargoAction?: VoyageCargoActionDetailsFragment) => {
	if (!cargoAction) {
		return "";
	} else if (cargoAction.cargo?.product) {
		return cargoAction.cargo.product.name;
	} else {
		return cargoAction.cargo?.containerNumber || cargoAction.cargo?.cargoNumber;
	}
};

interface CargoActionsProps {
	voyage: VoyageCargoActionDetailsVoyageFragment;
	cargoActions: VoyageCargoActionDetailsFragment[];
	selectedCall: VoyageDetailsProps["voyage"]["calls"][number] | undefined;
	selectedAction: VoyageCargoActionDetailsFragment | undefined;
	setSelectedActionId: (id: number | null) => void;
	setSelectedCallId: (id: number | null) => void;
	refetch: () => void;
}

const CargoActions = ({
	voyage,
	cargoActions,
	selectedCall,
	selectedAction,
	setSelectedCallId,
	setSelectedActionId,
	refetch,
}: CargoActionsProps) => {
	const handleCargoStatusChange = (newStatus: StatusFragment) => {
		// when changing a status we should switch to the relevant action (for example when going from a loading to a discharging status)
		const cargoId = selectedAction?.cargo?.id;

		if (!cargoId) {
			return;
		}

		// when changing a status to cargo completed, we should switch to the next call if the voyage has remaining calls
		if (newStatus.finishedStatus) {
			const orderedCalls = [ ...voyage.calls ].sort((a, b) => (a.pta ?? 0) - (b.pta ?? 0));
			const currentCallId = voyage.calls.find(c => c.cargos?.some(a => a.id === selectedAction?.id))?.id;

			const currentIndex = orderedCalls.findIndex(c => c.id === currentCallId);
			const nextCall = currentIndex >= 0 ? orderedCalls[currentIndex + 1] : undefined;

			if (nextCall) {
				setSelectedCallId(nextCall.id);
				setSelectedActionId(null);
			}
			return;
		}

		if (!newStatus.cargoActionType || selectedAction?.type === newStatus.cargoActionType) {
			return;
		}

		for (const call of voyage.calls) {
			const matchingAction = call.cargos?.find(
				a => a.cargo?.id === cargoId && a.type === newStatus.cargoActionType,
			);

			if (matchingAction && matchingAction.id !== null) {
				setSelectedCallId(call.id);
				setSelectedActionId(matchingAction.id);
				return;
			}
		}
	};

	useEffect(() => {
		if (cargoActions.length === 0 || cargoActions[0].id == null) {
			return;
		}
		if (selectedAction) {
			setSelectedActionId((cargoActions.find(a => a.id === selectedAction.id) || cargoActions[0]).id ?? undefined);
		} else {
			setSelectedActionId(cargoActions[0]?.id);
		}
	}, [ cargoActions, selectedAction ]);

	const sdsDocument = selectedAction?.cargo?.product?.documents?.find(it => it.documentType === "SDS" && it.active);

	const actionsForSelectedCargo = voyage.calls.flatMap(c => c.cargos)
		.filter(ca => ca.cargo.id === selectedAction?.cargo?.id);

	const siblingCargos = selectedAction?.cargo
		? cargoActions
			.filter(a => a.id !== selectedAction.id && a.cargo != null)
			.filter(a => a.cargo!.lastStatusChange?.status?.id === selectedAction.cargo?.lastStatusChange?.status?.id)
			.map(a => a.cargo! as StatusCargoEntity)
		: [];

	return (
		<>
			<Card
				disablePadding
				icon={<Widgets fontSize="small" color="primary" />}
				title={getCargoActionLabel(selectedAction)}
				subtitle={`${selectedAction ? intl.formatMessage({ id: selectedAction.type.toString() }) : null}`}
				actions={[ {
					render: () => selectedAction ? (
						<Box marginRight={1}>
							<StatusChanger
								session={useStoreState(store => store.session)}
								entity={{ ...selectedAction.cargo, actions: actionsForSelectedCargo }}
								siblingEntities={siblingCargos}
								statusType={StatusType.Cargo}
								refetch={refetch}
								callTimes={selectedCall}
								showHistory
								onStatusChange={handleCargoStatusChange}
							/>
						</Box>
					) : undefined,
				}, {
					render: () => selectedAction?.cargo && <TransportDocumentPdfButton cargo={selectedAction?.cargo} />,
				}, {
					render: () => sdsDocument && <SdsDocumentButton documentId={sdsDocument.id} />,
				} ]}>
				{selectedAction && (
					<Grid container spacing={2}>
						<CargoActionDetails
							cargoAction={selectedAction}
							lastShippedProductsFreeText={voyage.lastShippedProductsFreeText}
							refetch={refetch}
						/>
					</Grid>
				)}
			</Card>
		</>
	);
};

interface CargoActionDetailsProps {
	cargoAction: VoyageCargoActionDetailsFragment
	lastShippedProductsFreeText: string[];
	refetch: () => void;
}

const CargoActionDetails = ({ cargoAction, refetch, lastShippedProductsFreeText }: CargoActionDetailsProps) => {
	const { isRTR } = usePermission();

	const { cargo } = cargoAction;
	if (cargo == null) {
		return <></>;
	}

	return (
		<Grid size={12} sx={{ padding: 2 }} overflow={"auto"}>
			<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "PRODUCT" })}>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<LastShippedProductsPopover lastShippedProducts={lastShippedProductsFreeText}>
						<ProductLink product={cargo.product} />
					</LastShippedProductsPopover>
				</Box>
			</FormGroup>

			{cargo.supplier && (
				<FormGroup
					labelPlacement={"left-center"}
					label={intl.formatMessage({ id: "SUPPLIER" })}>
					{isRTR ? <RelationLink relation={cargo.supplier} /> : cargo.supplier?.name}
				</FormGroup>
			)}

			{(cargoAction.type === CallType.Loading || cargoAction.type == CallType.Discharging) && (
				<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "QUANTITY" })}>
					<CargoActionQuantity cargoAction={cargoAction} cargo={cargoAction.cargo} />
				</FormGroup>
			)}

			{cargoAction.type === CallType.NitrogenPurging && (
				<FormGroup
					labelPlacement="left-center"
					label={intl.formatMessage({ id: "CARGO_ACTION_LIMIT" })}
				>
					{cargoAction?.limitValue ?? "-"}°C {" "}
					{cargoAction?.limitType
						? intl.formatMessage({ id: `LIMIT_TYPE_${cargoAction.limitType}` })
						: ""}
				</FormGroup>
			)}

			{cargoAction.location && (
				<FormGroup
					labelPlacement={"left-center"}
					label={intl.formatMessage({ id: "LOCATION" })}>
					<VoyagePhoneContactsPopover contacts={cargoAction.location.contacts ?? []}>
						<LocationLink location={cargoAction.location} />
					</VoyagePhoneContactsPopover>
				</FormGroup>
			)}

			{cargoAction.connectingShipPortVisit && (
				<FormGroup
					labelPlacement={"left-center"}
					label={intl.formatMessage({ id: "CONNECTING_SHIP" })}>
					<ConnectingVisitField action={cargoAction} />
				</FormGroup>
			)}

			{!cargoAction.simulatedTimestamp && (
				<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "DATE" })}>
					<CargoActionDate action={cargoAction} />
				</FormGroup>
			)}

			{cargoHasServices(cargo) && (
				<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "CARGO_SERVICES" })}>
					<CargoServiceIcons cargo={cargo} size="small" />
				</FormGroup>
			)}

			{(cargoAction.type === CallType.Loading || cargoAction.type == CallType.Discharging) && cargo.weightToleranceType && (
				<>
					<FormGroup
						labelPlacement={"left-center"}
						label={intl.formatMessage({ id: "WEIGHT_TOLERANCE_TYPE" })}>
						{intl.formatMessage({ id: `WEIGHT_TOLERANCE_TYPE_${cargo.weightToleranceType}` })}
					</FormGroup>

					{cargo.weightOptionType && (
						<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "WEIGHT_OPTION_TYPE" })}>
							{intl.formatMessage({ id: `WEIGHT_OPTION_${cargo.weightOptionType}` })}
						</FormGroup>
					)}
					{cargo.weightOptionPercentage && (
						<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "WEIGHT_OPTION_PERCENTAGE" })}>
							{DataFormat.percentage(cargo.weightOptionPercentage)}
						</FormGroup>
					)}
					{cargo.declaredFinalWeight && (
						<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "DECLARED_FINAL_WEIGHT" })}>
							{cargo.declaredFinalWeight} {intl.formatMessage({ id: "UNIT_MTON" })}
						</FormGroup>
					)}
				</>
			)}

			{cargoAction.reference && (
				<FormGroup
					label={intl.formatMessage({ id: "REFERENCE" })}
					labelPaddingTop={0}
					labelPlacement={"left"}>
					<ExpandableText
						text={cargoAction.reference} />
				</FormGroup>
			)}

			{cargoAction.notes && (
				<FormGroup
					labelPlacement={"left"}
					labelPaddingTop={0}
					label={intl.formatMessage({ id: "NOTE" })}>
					<ExpandableText
						text={cargoAction.notes}
					/>
				</FormGroup>
			)}

			{cargoAction.location?.notes && (
				<FormGroup
					label={intl.formatMessage({ id: "LOCATION_NOTES_VOYAGE_INFO" })}
					labelPaddingTop={0}
					labelPlacement={"left"}>
					<ExpandableText
						text={cargoAction.location?.notes} />
				</FormGroup>
			)}

			{cargoAction.surveyor && (
				<FormGroup
					labelPlacement={"left-center"}
					label={intl.formatMessage({ id: "SURVEYOR" })}
				>
					<VoyagePhoneContactsPopover contacts={cargoAction.surveyor.contacts ?? []}>
						{isRTR ? <RelationLink relation={cargoAction.surveyor} /> : cargoAction.surveyor?.name}
					</VoyagePhoneContactsPopover>
				</FormGroup>
			)}

			{(cargoAction.type === CallType.Loading && cargo.loadingReference) || (cargoAction.type == CallType.Discharging && cargo.dischargingReference) && (
				<FormGroup
					labelPlacement={"left-center"}
					label={intl.formatMessage({ id: `CARGO_${cargoAction.type}_REFERENCE` })}>
					{cargo.loadingReference ?? cargo.dischargingReference}
				</FormGroup>
			)}

			{cargo?.product?.productInstructionsForVessel && (
				<FormGroup
					labelPlacement={"left"}
					labelPaddingTop={0}
					label={intl.formatMessage({ id: "PRODUCT_INSTRUCTIONS" })}>
					<Linkify
						options={{
							target: "_blank",
							rel: "noreferrer noopener",
							validate: {
								email: false,
							},
						}}>
						<span style={{ whiteSpace: "pre-wrap" }}>{cargo?.product?.productInstructionsForVessel}</span>
					</Linkify>
				</FormGroup>
			)}

			{(cargoAction.type === CallType.Loading || cargoAction.type == CallType.Discharging) && cargo.product?.heatingInstructionsForVessel && (
				<FormGroup
					labelPlacement={"left"}
					labelPaddingTop={0}
					label={intl.formatMessage({ id: "HEATING_INSTRUCTIONS" })}>
					<span style={{ whiteSpace: "pre-wrap" }}>{cargo.product?.heatingInstructionsForVessel}</span>
				</FormGroup>
			)}

			{cargo.product?.isDangerousGood && (
				<FormGroup
					labelPlacement={"left-center"}
					label={intl.formatMessage({ id: "PRODUCTS_DANGEROUS_GOOD" })}>
					{DataFormat.boolean(true)}
				</FormGroup>
			)}

			{cargoAction.location?.uabRegistration && (
				<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "UAB_REGISTRATION" })}>
					{DataFormat.boolean(cargoAction.location?.uabRegistration)}
				</FormGroup>
			)}

			{[ CallType.Loading, CallType.Discharging ].includes(cargoAction.type) && (
				<CargoActionActuals
					cargoAction={cargoAction}
					refetch={refetch}
				/>
			)}
		</Grid>
	);
};

const CargoActionActuals = ({ cargoAction, refetch }: Pick<CargoActionDetailsProps, "cargoAction" | "refetch">) => {
	const layout = useLayout();
	const session = useStoreState(state => state.session);
	const { cargo, measurements } = cargoAction;

	const { isRTR, isSkipperOrShipManager, isReadOnly, isOperator } = usePermission();
	const canEditCargoActuals = (isRTR || isSkipperOrShipManager || isOperator) && !isReadOnly && cargoAction.type !== CallType.NitrogenPurging && cargoAction.type !== CallType.Cleaning;
	const canEditProtest = (isRTR || isSkipperOrShipManager) && !isReadOnly && cargoAction.type !== CallType.NitrogenPurging && cargoAction.type !== CallType.Cleaning;

	const [ open, setOpen ] = useState(false);
	const [ lopDialogOpen, openLopDialog ] = useState(false);

	const [ saveCargoMeasurements, { loading } ] = useMutation(SaveCargoMeasurementsDocument);

	const openDialog = () => {
		layout.closeDrawer();
		openLopDialog(true);
	};

	const onSave = (action: CargoActualsOutput) => {
		if (action.id == null) {
			return;
		}
		saveCargoMeasurements({
			variables: {
				cargoId: cargo!.id, // we will have a cargo
				actionId: action.id,
				input: action.measurements ?? {},
			},
		}).then(() => {
			setOpen(false);
			refetch();
		});
	};

	if (cargo == null) {
		return <></>;
	}

	return <>
		<div className="header" style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
			<div className="title" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
				<Balance fontSize="small" color="primary" />

				<Typography variant="h3" sx={{ fontSize: 16, fontWeight: 500 }} color="textPrimary">
					{intl.formatMessage({ id: cargoAction.type })} {intl.formatMessage({ id: "ACTUALS" })}
				</Typography>
			</div>
			<div className="actions" style={{ display: "flex", gap: 8 }}>
				{(measurements?.protest && canEditProtest) && (
					<Button
						icon={<SdCardAlert fontSize="small" />}
						variant="text"
						size="small"
						onClick={() => openDialog()}
					/>
				)}
				{canEditCargoActuals && (
					<Button
						icon={<Edit fontSize="small" />}
						variant="text"
						size="small"
						onClick={() => setOpen(true)}
					/>
				)}
			</div>
		</div>

		{(measurements?.actualConsignor ?? cargo.consignor) && (
			<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "CARGO_CONSIGNOR" })}>
				{isRTR
					? measurements?.actualConsignor ? measurements?.actualConsignor : <RelationLink relation={cargo.consignor} />
					: measurements?.actualConsignor ?? cargo.consignor?.name
				}
			</FormGroup>
		)}

		{(measurements?.actualConsignee ?? cargo.consignee) && (
			<FormGroup labelPlacement={"left-center"} label={intl.formatMessage({ id: "ORDER_CONSIGNEE" })}>
				{isRTR
					? measurements?.actualConsignee ? measurements?.actualConsignee : <RelationLink relation={cargo.consignee} />
					: measurements?.actualConsignee ?? cargo.consignee?.name
				}
			</FormGroup>
		)}

		{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "landMeasurement") && (
			<FormGroup label={intl.formatMessage({ id: "CARGO_ACTUALS_LANDSIDE" })} labelPlacement={"left-center"}>
				{(measurements?.locationVolume) ?
					DataFormat.volumeWithUnit(measurements?.locationVolume, measurements?.locationVolumeUnit) : "-"
				}
				<span> / </span>
				{measurements?.locationWeight ?
					DataFormat.mtonWeightWithUnit(
						measurements?.locationWeight,
						measurements?.locationWeightUnit,
					) : "-"
				}
			</FormGroup>
		)}

		{hasOptionalFieldPermission(session, PluginFields.cargoAction, "shipMeasurement") &&
			<FormGroup label={intl.formatMessage({ id: "CARGO_ACTUALS_SHIPSIDE" })} labelPlacement={"left-center"}>
				{measurements?.skipperVolume ?
					DataFormat.volumeWithUnit(measurements?.skipperVolume, measurements?.skipperVolumeUnit) : "-"
				}
				<span> / </span>
				{measurements?.skipperWeight ?
					DataFormat.mtonWeightWithUnit(
						measurements?.skipperWeight,
						measurements?.skipperWeightUnit,
					) : "-"}
			</FormGroup>
		}

		{hasOptionalFieldPermission(session, PluginFields.cargoAction, "landMeasurement") &&
			hasOptionalFieldPermission(session, PluginFields.cargoAction, "shipMeasurement") && (
			<FormGroup label={intl.formatMessage({ id: "DIFFERENCE" })} labelPlacement={"left-center"}>
				{(measurements?.locationVolume) ? (
					DataFormat.percentage(cargoActualsComparison(measurements?.skipperVolume, measurements?.locationVolume)?.percentage, 2, true)
				) : "-"}
				<span> / </span>
				{measurements?.skipperWeight ? (
					DataFormat.percentage(cargoActualsComparison(measurements?.skipperWeight, measurements?.locationWeight)?.percentage, 2, true)
				) : "-" }
			</FormGroup>
		)}

		{measurements?.ambientVolume && (
			<FormGroup
				labelPlacement={"left-center"}
				label={intl.formatMessage({ id: "AMBIENT_VOLUME" })}>
				{DataFormat.simpleUnit(measurements?.ambientVolume, DataFormat.UNITS.LITER)}
			</FormGroup>
		)}

		{measurements?.relativeDensity && (
			<FormGroup
				labelPlacement={"left-center"}
				label={intl.formatMessage({ id: `RELATIVE_DENSITY_AT_${cargoAction.type}` })}>
				{measurements?.relativeDensity.toFixed(4)} {DataFormat.UNITS.DENSITY}
			</FormGroup>
		)}

		{measurements?.productTemperature && (
			<FormGroup
				labelPlacement={"left-center"}
				label={intl.formatMessage({ id: `PRODUCT_${cargoAction.type}_TEMPERATURE` })}>
				{DataFormat.simpleUnit(measurements?.productTemperature, DataFormat.UNITS.CELSIUS)}
			</FormGroup>
		)}

		<FormDialog
			open={open}
			onClose={() => setOpen(false)}
			title={(
				intl.formatMessage({ id: "X_ACTION_FOR_Y_PRODUCT_AT_Z_LOCATION" })
					.replace("_X_", intl.formatMessage({ id: `LOAD_OR_DISCHARGE_${cargoAction.type}` }))
					.replace("_Y_", (cargo.cargoNumber ? `${intl.formatMessage({ id: "CARGO" })} ${cargo.cargoNumber}` : null) || cargo.product?.name || cargo.containerNumber || "")
					.replace("_Z_", cargoAction.location?.description || "")
			)}
			form="cargoActionForm"
			maxWidth="md"
			loading={loading}
		>
			<CargoActualsForm
				action={cargoAction}
				onSave={(data) => onSave(data)}
			/>
		</FormDialog>

		{canEditProtest &&
			<LetterOfProtestDialog
				open={lopDialogOpen}
				onClose={openLopDialog}
				cargoActionId={cargoAction.id} />}
	</>;
};

const CallTimes = ({ call, refetch }: { call: VoyageCallTimesBlockFragment; refetch: () => void; }) => {
	const { isSkipperOrShipManager } = usePermission();
	const [ dialogOpen, openDialog ] = useState(false);
	const [ historyDialogOpen, openHistoryDialog ] = useState(false);

	return <Card
		icon={<AirlineStops fontSize="small" color="primary" />}
		title={<LocationLink location={call.location} />}
		subtitle={intl.formatMessage({ id: "CALL_TIMES" })}
		actions={[
			{
				icon: <Edit fontSize="small" />,
				tooltip: intl.formatMessage({ id: "EDIT" }),
				onClick: () => openDialog(true),
			},
			{
				icon: <History fontSize="small" />,
				tooltip: intl.formatMessage({ id: "HISTORY" }),
				onClick: () => openHistoryDialog(true),
				hidden: isSkipperOrShipManager,
			},
		]}
	>
		<VoyageCallTimesDetails call={call} />

		<VoyageCallUpdateTimesAndLocationDialog
			open={dialogOpen}
			onClose={() => openDialog(false)}
			call={call}
			refetch={refetch}
		/>

		<EntityHistoryDialog
			open={historyDialogOpen}
			onClose={() => openHistoryDialog(false)}
			entity={HistoryEntity.Call}
			entityId={call.id}
			title={intl.formatMessage({ id: "HISTORY" })}
		/>
	</Card>;
};

export default VoyageInfo;
